import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useMount } from 'react-use';

import {
   HistoricalPrescriptionAuthenticationFault,
   PrescriptionOperationInvalidRequestFault,
   PrescriptionP1HistoryDailyGroup,
   PrescriptionRetrieveP1HistoryCommand,
} from '@dreryk/egabinet-prescription-frontend-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';
import { ServiceLocationId } from '@dreryk/egabinet-healthcare-provider-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { commonTranslationKeys, TranslatableText } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { PatientDataAccessMode } from '~libs.shared.prescription';

import { prescriptionsHistoryDownloadTranslationKeys } from '../translations';

import { LatestHundredPrescriptionRecordsText } from './LatestHundredPrescriptionRecordsText';
import { P1PrescriptionsHistoryTable } from './P1PrescriptionsHistoryTable';
import { countPrescriptions, getHistoricalPrescriptionDataAccessMode } from './PrescriptionsHistoryView.utils';
import { StyledPrescriptionsHistory } from './PrescriptionsHistoryView.styled';

type PrescriptionsHistoryViewProps = {
   readonly onFault: (errorMessage?: TranslatableText) => void;
   readonly patientId: number;
   readonly locationId: ServiceLocationId;
   readonly accessMode: PatientDataAccessMode | undefined;
};

const LATEST_HUNDRED_PRESCRIPTION_RECORDS_TEXT_DISPLAY_THRESHOLD = 100;

export const PrescriptionsHistoryView = (props: PrescriptionsHistoryViewProps): ReactElement => {
   const [historicalPrescriptions, setHistoricalPrescriptions] = useState<PrescriptionP1HistoryDailyGroup[]>();
   const {
      executeAsync: retrieveHistoricalPrescriptions,
      loading: retrieveHistoricalPrescriptionsLoading,
      error: retrieveHistoricalPrescriptionsError,
   } = useCqrsCommand<
      PrescriptionP1HistoryDailyGroup[],
      void,
      HistoricalPrescriptionAuthenticationFault | PrescriptionOperationInvalidRequestFault
   >(
      () => {
         const command = new PrescriptionRetrieveP1HistoryCommand();
         command.patientId = PatientId.of(props.patientId);
         command.locationId = props.locationId;
         command.dataAccessMode = Slot.emptyIfNull(getHistoricalPrescriptionDataAccessMode(props.accessMode ?? null));
         return command;
      },
      {
         onFault: {
            'egabinet-prescription-frontend-api:HistoricalPrescriptionAuthenticationFault': () =>
               props.onFault(prescriptionsHistoryDownloadTranslationKeys.historicalPrescriptionAuthenticationFault),
            'egabinet-prescription-frontend-api:PrescriptionOperationInvalidRequestFault': () => {
               dispatchSnackbarNegativeEvent(commonTranslationKeys.unexpectedErrorHasOccurred);
               props.onFault();
            },
         },
         onUnexpectedError: () => {
            // TODO: DREGAB-12414 - Obsługa błędów w przypadku gdy P1 nie działa
            dispatchSnackbarNegativeEvent(commonTranslationKeys.unexpectedErrorHasOccurred);
            props.onFault();
         },
      }
   );

   const refreshHistoricalPrescriptions = useCallback(async () => {
      const historicalPrescriptions = await retrieveHistoricalPrescriptions();
      setHistoricalPrescriptions(historicalPrescriptions);
   }, [retrieveHistoricalPrescriptions]);

   useMount(refreshHistoricalPrescriptions);

   const dailyGroups = useMemo(() => historicalPrescriptions ?? [], [historicalPrescriptions]);

   if (retrieveHistoricalPrescriptionsLoading) {
      return <LoaderSpinner />;
   }

   if (retrieveHistoricalPrescriptionsError) {
      return (
         <Message
            testId={'prescriptionsHistoryFetchError'}
            type={MessageType.NEGATIVE}
            label={prescriptionsHistoryDownloadTranslationKeys.prescriptionsHistoryFetchError}
         />
      );
   }

   const shouldDisplayInfo =
      historicalPrescriptions &&
      countPrescriptions(historicalPrescriptions) >= LATEST_HUNDRED_PRESCRIPTION_RECORDS_TEXT_DISPLAY_THRESHOLD;

   return (
      <StyledPrescriptionsHistory direction={'column'} gap={'12px'}>
         {shouldDisplayInfo ? <LatestHundredPrescriptionRecordsText /> : null}
         <P1PrescriptionsHistoryTable
            dailyGroups={dailyGroups}
            patientId={props.patientId}
            onMedicineProcessed={refreshHistoricalPrescriptions}
         />
      </StyledPrescriptionsHistory>
   );
};
